import { Spacer } from '@chakra-ui/react'
import { Flex, Text } from '@chakra-ui/react'
import { Phone, PhoneOutgoing, Plus, SquareParking } from 'lucide-react'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { PulsingCircleBadge } from '~/components/shared/PulsingCircleBadge'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { TextButton } from '~/components/shared/TextButton'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'
import { PresenceState } from '~/utils/xml'

import { toNounCasing } from '@mangovoice/core'
import { EmptyState } from '@mangovoice/mango-ui'
import { ContactSourceType } from '@mangovoice/types'

import { useEmptyListItemStyle } from './componentStyles/EmptyListItem.style'
import {
  QUICK_DIAL_ACTION_BUTTONS_CLASS_NAME,
  useQuickDialListItemStyle,
} from './componentStyles/QuickDialListItem.style'
import { getPresenceLabel, PresenceKind } from './utils/presenceStatus'
import { normalizeWhitespace } from './utils/stringUtils'

export const enum ListItemDataType {
  ParkingLot = 'parkingLot',
  EmptyParkingLot = 'emptyParkingLot',
  Extension = 'extension',
  EmptyExtension = 'emptyExtension',
  Contact = 'contact',
  SpeedDial = 'speedDial',
  EmptySpeedDial = 'emptySpeedDial',
}

type EmptyParkingItemData = {
  dataType: ListItemDataType.EmptyParkingLot
  id: number
}
type EmptyLineExtensionItemData = {
  dataType: ListItemDataType.EmptyExtension
  id: number
}
type EmptyListItemData = EmptyParkingItemData | EmptyLineExtensionItemData
type ParkingPresenceListItemData = {
  dataType: ListItemDataType.ParkingLot
  id: number
  label: string
  targetNumber: string
  presence?: PresenceState
}
type LineExtensionListItemData = {
  dataType: ListItemDataType.Extension
  id: number
  name: string
  number: string
  presence?: PresenceState
}
export type RecentlyDialedListItemData = {
  dataType: ListItemDataType.Contact
  id: number
  contact: ContactDetails
}
export type SpeedDialListItemData = {
  dataType: ListItemDataType.SpeedDial
  id: number
  label: string
  number: string
}
export type EmptySpeedDialListItemData = {
  dataType: ListItemDataType.EmptySpeedDial
  id: number
}
export type PresenceListItemData =
  ParkingPresenceListItemData | LineExtensionListItemData
export type ListItemData =
  | PresenceListItemData
  | RecentlyDialedListItemData
  | EmptyListItemData
  | SpeedDialListItemData
  | EmptySpeedDialListItemData
const getPresenceBadge = (label: string, presence?: PresenceState) => {
  switch (presence) {
    case PresenceState.Available:
      return <CircleBadge variant={CircleBadgeVariant.Green} label={label} />
    case PresenceState.Connecting:
      return (
        <PulsingCircleBadge variant={CircleBadgeVariant.Red} label={label} />
      )
    case PresenceState.Unavailable:
      return <CircleBadge variant={CircleBadgeVariant.Red} label={label} />
    default:
      return <CircleBadge variant={CircleBadgeVariant.Gray} label={label} />
  }
}

interface BaseListItemProps {
  presenceBadge?: React.ReactNode
  extensionBadge?: React.ReactNode
  label: string
  subLabel?: string
  buttons: React.ReactNode
}
const BaseListItem = ({
  presenceBadge,
  extensionBadge,
  label,
  subLabel,
  buttons,
}: BaseListItemProps) => {
  const styles = useQuickDialListItemStyle()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    justifyContent: styles.container?.justifyContent,
    alignItems: styles.container?.alignItems,
    gap: styles.container?.gap,
  }

  const textContainerProps = {
    flexDirection: styles.textContainer?.flexDirection,
    justifyContent: styles.textContainer?.justifyContent,
    gap: styles.textContainer?.gap,
  }

  const labelContainerProps = {
    alignItems: styles.labelContainer?.alignItems,
    gap: styles.labelContainer?.gap,
  }

  const buttonsContainerProps = {
    display: styles.buttonsContainer?.display,
    gap: styles.buttonsContainer?.gap,
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      {presenceBadge}
      <Flex css={styles.textContainer} {...textContainerProps}>
        <Flex css={styles.labelContainer} {...labelContainerProps}>
          {extensionBadge}
          <Text css={styles.labelText} lineClamp={1}>
            {label}
          </Text>
        </Flex>
        {subLabel && (
          <Text css={styles.subLabelText} lineClamp={1}>
            {subLabel}
          </Text>
        )}
      </Flex>
      <Spacer />
      <Flex
        css={styles.buttonsContainer}
        className={QUICK_DIAL_ACTION_BUTTONS_CLASS_NAME}
        {...buttonsContainerProps}
      >
        {buttons}
      </Flex>
    </Flex>
  )
}

type EmptyListItemProps = {
  onSettingsLinkClick: () => void
}
export const EmptyParkingLotListItem = ({
  onSettingsLinkClick,
}: EmptyListItemProps) => {
  const styles = useEmptyListItemStyle()
  const { t } = useTranslation()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    justifyContent: styles.container?.justifyContent,
    alignItems: styles.container?.alignItems,
    gap: styles.container?.gap,
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      <Text css={styles.text}>{t('quickDialListItem.empty.parkingSpots')}</Text>
      <TextButton onLinkClick={onSettingsLinkClick}>
        {t('quickDialListItem.button.webPhoneSettings')}
      </TextButton>
    </Flex>
  )
}
export const EmptyLineExtensionListItem = ({
  onSettingsLinkClick,
}: EmptyListItemProps) => {
  const styles = useEmptyListItemStyle()
  const { t } = useTranslation()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    justifyContent: styles.container?.justifyContent,
    alignItems: styles.container?.alignItems,
    gap: styles.container?.gap,
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      <Text css={styles.text}>
        {t('quickDialListItem.empty.lineExtensions')}
      </Text>
      <TextButton onLinkClick={onSettingsLinkClick}>
        {t('quickDialListItem.button.configurePresence')}
      </TextButton>
    </Flex>
  )
}
export const EmptySpeedDialListItem = ({
  onSettingsLinkClick,
}: EmptyListItemProps) => {
  const styles = useEmptyListItemStyle()
  const { t } = useTranslation()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    justifyContent: styles.container?.justifyContent,
    alignItems: styles.container?.alignItems,
    gap: styles.container?.gap,
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      <Text css={styles.text}>{t('quickDialListItem.empty.speedDial')}</Text>
      <TextButton onLinkClick={onSettingsLinkClick}>
        {t('quickDialListItem.button.configureSpeedDial')}
      </TextButton>
    </Flex>
  )
}

export const EmptyRecentlyDialedListItem = () => {
  const { t } = useTranslation()

  return (
    <EmptyState
      icon={<Phone />}
      title={t('quickDialListItem.empty.recentlyDialedPrimary')}
      description={t('quickDialListItem.empty.recentlyDialedSecondary')}
    />
  )
}

type ListItemProps = {
  hasActiveCall: boolean
  showNewCallButton: boolean
  onTransfer: (parkTarget: string) => void
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  onNewCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
}

type ParkingLotListItemProps = Omit<ListItemProps, 'showNewCallButton'> & {
  itemData: ParkingPresenceListItemData
}
export const ParkingLotListItem = ({
  itemData,
  hasActiveCall,
  onTransfer,
  onStartCall,
}: ParkingLotListItemProps) => {
  const { t } = useTranslation()
  const buttons: ReactNode[] = []
  const status = getPresenceLabel(t, itemData.presence, PresenceKind.Parking)
  const presenceBadge = getPresenceBadge(status, itemData.presence)
  const label = itemData.label
  const callee = itemData.targetNumber
  const isOccupied = itemData.presence === PresenceState.Unavailable
  const labelOptions = { name: normalizeWhitespace(label), status }

  // Context-aware button: show one clear action based on current state
  if (isOccupied) {
    // Spot occupied - only show retrieve button
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-pickup`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={Phone}
        tooltipText={t('quickDialListItem.tooltip.pickUpCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.pickUpCall', {
          defaultValue: 'Pick up call: {{name}}, {{status}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
      />,
    )
  } else if (hasActiveCall) {
    // Spot empty with active call - only show park button
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-park`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={SquareParking}
        tooltipText={t('quickDialListItem.tooltip.parkCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.parkCall', {
          defaultValue: 'Park call: {{name}}, {{status}}',
          ...labelOptions,
        })}
        onClick={() => onTransfer(callee)}
      />,
    )
  } else {
    // Spot empty, no call - show start call button
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={Phone}
        tooltipText={t('quickDialListItem.tooltip.startCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.startCallWithStatus', {
          defaultValue: 'Start call: {{name}}, {{status}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <BaseListItem
      presenceBadge={presenceBadge}
      label={label}
      buttons={buttons}
    />
  )
}

type LineExtensionListItemProps = ListItemProps & {
  itemData: LineExtensionListItemData
}
export const LineExtensionListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onTransfer,
  onStartCall,
}: LineExtensionListItemProps) => {
  const { t } = useTranslation()
  const buttons: ReactNode[] = []
  const status = getPresenceLabel(t, itemData.presence, PresenceKind.Extension)
  const presenceBadge = getPresenceBadge(status, itemData.presence)
  const extensionBadge = (
    <ExtensionBadge
      size={Size.sm}
      variant={ExtensionBadgeVariant.LightGray}
      extension={itemData.number}
    />
  )
  const label = itemData.name
  const callee = itemData.number
  const labelOptions = {
    name: normalizeWhitespace(label) || callee,
    status,
  }

  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-transfer`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={PhoneOutgoing}
        tooltipText={t('quickDialListItem.tooltip.transferCall')}
        accessibleLabel={t(
          'quickDialListItem.ariaLabel.transferCallWithStatus',
          {
            defaultValue: 'Transfer call: {{name}}, {{status}}',
            ...labelOptions,
          },
        )}
        onClick={() => onTransfer(callee)}
      />,
      <QuickActionButton
        key={`${itemData.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={Plus}
        tooltipText={t('quickDialListItem.tooltip.newCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.newCallWithStatus', {
          defaultValue: 'Start a new call: {{name}}, {{status}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={Phone}
        tooltipText={t('quickDialListItem.tooltip.startCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.startCallWithStatus', {
          defaultValue: 'Start call: {{name}}, {{status}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <BaseListItem
      presenceBadge={presenceBadge}
      extensionBadge={extensionBadge}
      label={label}
      buttons={buttons}
    />
  )
}

type RecentlyDialedListItemProps = ListItemProps & {
  itemData: RecentlyDialedListItemData
}
export const RecentlyDialedListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onTransfer,
  onStartCall,
}: RecentlyDialedListItemProps) => {
  const { t } = useTranslation()
  const contact = itemData.contact
  const buttons: ReactNode[] = []
  let extensionBadge: ReactNode | undefined
  let label: string
  let subLabel: string | undefined

  if (contact.numbers === undefined) {
    // No numbers for contact, no label/no button
    return <></>
  }

  const callee = { contact: contact, contactNumber: contact.defaultNumber }
  if (contact.source === ContactSourceType.Extensions) {
    // Extension, need ExtensionBadge/no label
    extensionBadge = (
      <ExtensionBadge
        size={Size.sm}
        variant={ExtensionBadgeVariant.LightGray}
        extension={contact.defaultNumber.number}
      />
    )
    label = contact.name
  } else if (contact.id === 0) {
    // Non-contact number from recents
    label = formatPhoneNumber(contact.defaultNumber.number)
  } else {
    // Regular contact
    label = contact.name
    subLabel = `${toNounCasing(
      contact.defaultNumber.label ?? '',
    )}: ${formatPhoneNumber(contact.defaultNumber.number)}`
  }

  const labelOptions = { name: normalizeWhitespace(label) }

  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${contact.id}-transfer`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={PhoneOutgoing}
        tooltipText={t('quickDialListItem.tooltip.transferCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.transferCall', {
          defaultValue: 'Transfer call: {{name}}',
          ...labelOptions,
        })}
        onClick={() => onTransfer(callee.contactNumber.number)}
      />,
      <QuickActionButton
        key={`${contact.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={Plus}
        tooltipText={t('quickDialListItem.tooltip.newCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.newCall', {
          defaultValue: 'Start a new call: {{name}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${contact.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={Phone}
        tooltipText={t('quickDialListItem.tooltip.startCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.startCall', {
          defaultValue: 'Start call: {{name}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <BaseListItem
      extensionBadge={extensionBadge}
      label={label}
      subLabel={subLabel}
      buttons={buttons}
    />
  )
}
type SpeedDialListItems = ListItemProps & {
  itemData: SpeedDialListItemData
}
type SpeedDialListItemProps = Omit<SpeedDialListItems, 'onTransfer'>

export const SpeedDialListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onStartCall,
}: SpeedDialListItemProps) => {
  const { t } = useTranslation()
  const styles = useQuickDialListItemStyle()
  const buttons: ReactNode[] = []

  const label = itemData.label

  const callee = itemData.number

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    justifyContent: styles.container?.justifyContent,
    alignItems: styles.container?.alignItems,
    gap: styles.container?.gap,
  }

  const textContainerProps = {
    flexDirection: styles.textContainer?.flexDirection,
    justifyContent: styles.textContainer?.justifyContent,
    gap: styles.textContainer?.gap,
  }

  const labelContainerProps = {
    alignItems: styles.labelContainer?.alignItems,
    gap: styles.labelContainer?.gap,
  }

  const buttonsContainerProps = {
    display: styles.buttonsContainer?.display,
    gap: styles.buttonsContainer?.gap,
  }

  const labelOptions = { name: normalizeWhitespace(label) || callee }

  if (hasActiveCall) {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-new-call`}
        variant={QuickActionButtonVariants.Default_100}
        actionIcon={Plus}
        tooltipText={t('quickDialListItem.tooltip.newCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.newCall', {
          defaultValue: 'Start a new call: {{name}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
        disabled={!showNewCallButton}
      />,
    )
  } else {
    buttons.push(
      <QuickActionButton
        key={`${itemData.id}-start-call`}
        variant={QuickActionButtonVariants.Default_300}
        actionIcon={Phone}
        tooltipText={t('quickDialListItem.tooltip.startCall')}
        accessibleLabel={t('quickDialListItem.ariaLabel.startCall', {
          defaultValue: 'Start call: {{name}}',
          ...labelOptions,
        })}
        onClick={() => onStartCall(callee)}
      />,
    )
  }

  return (
    <Flex css={styles.container} {...containerProps}>
      <Flex css={styles.textContainer} {...textContainerProps}>
        <Flex css={styles.labelContainer} {...labelContainerProps}>
          <Text css={styles.labelText} lineClamp={1}>
            {label}
          </Text>
        </Flex>
        {callee && (
          <Text
            css={{ ...styles.subLabelText, color: 'fg.muted' }}
            lineClamp={1}
          >
            {callee}
          </Text>
        )}
      </Flex>
      <Spacer />
      <Flex
        css={styles.buttonsContainer}
        className={QUICK_DIAL_ACTION_BUTTONS_CLASS_NAME}
        {...buttonsContainerProps}
      >
        {buttons}
      </Flex>
    </Flex>
  )
}
